import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation, withRouter, } from 'react-router';
import FileIcon from 'images/v5/icons/file-04.svg';
import FolderIcon from 'images/v5/icons/folder.svg';
import Bar from 'layout/atoms/Bar';
import Caption from 'layout/atoms/Caption';
import Subtitle from 'layout/atoms/Subtitle';
import Grid from 'layout/molecules/Grid/Grid';
import Sticky from 'layout/molecules/Sticky';
import FoldersTree from 'layout/organisms/FoldersTree/FoldersTree';
import Submenu from 'organisms/Submenu/Submenu';
import { clearBreadcrumbs, setBreadcrumbs } from 'store/layoutSlice';
import { fetchRootFolder, fetchRootFolders, selectFolderName, selectFoldersTree, } from 'store/specificationsFoldersSlice';
import Dashboard from 'templates/Dashboard/Dashboard';
import { getMenuLabel } from 'utils/getMenuItem';
import AllSpecifications from './AllSpecifications';
import Filters from './Filters/Filters';
import FolderCreator from './FolderCreator/FolderCreator';
import FolderView from './FolderView';
import HeaderAside from './HeaderAside/HeaderAside';
function SpecificationsDashboard({ match, url }) {
    var _a;
    const { t } = useTranslation(['specification', 'exceptions', 'common']);
    const dispatch = useDispatch();
    const location = useLocation();
    const layout = useSelector((state) => state.layout);
    const rootFolder = useSelector((state) => state.specificationsFolders.rootFolder);
    const refetchFlags = useSelector((state) => state.specificationsFolders.refetchFlags);
    const folderId = location.pathname.includes('/specifications/folders/')
        ? location.pathname.replace('/specifications/folders/', '')
        : rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId;
    const foldersTree = useSelector(selectFoldersTree);
    const folderName = useSelector((state) => selectFolderName(state, folderId));
    useEffect(() => {
        dispatch(fetchRootFolder());
    }, [dispatch]);
    useEffect(() => {
        if (refetchFlags.fetchList && (rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId)) {
            dispatch(fetchRootFolders({ folderId: rootFolder.rootFolderId }));
        }
    }, [dispatch, refetchFlags.fetchList, rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId]);
    useEffect(() => {
        if (rootFolder.rootFolderId !== null) {
            dispatch(fetchRootFolders({ folderId: rootFolder.rootFolderId }));
        }
    }, [dispatch, rootFolder.status, rootFolder.rootFolderId]);
    const defaultLinks = [
        {
            id: 'all_folders',
            to: `/specifications/folders/${rootFolder.rootFolderId}`,
            text: t('all_folders'),
            icon: React.createElement(FolderIcon, null),
        },
        {
            id: 'my_files',
            to: '/specifications/my-files',
            text: t('my_files'),
            icon: React.createElement(FileIcon, null),
        },
    ];
    const folderBreadcrumbs = useMemo(() => {
        if (!foldersTree)
            return [];
        const breadcrumbs = [];
        let searchId = folderId;
        while (searchId) {
            // eslint-disable-next-line no-loop-func
            const folder = foldersTree.find((f) => f.id === searchId);
            if (!folder)
                break;
            breadcrumbs.unshift({
                id: folder.id,
                to: `/specifications/folders/${folder.id}`,
                name: folder.name,
            });
            searchId = folder.parentId;
        }
        return breadcrumbs;
    }, [foldersTree, folderId]);
    // Update breadcrumbs based on current location
    useEffect(() => {
        const specificationsLabel = getMenuLabel('/specifications', t);
        if (location.pathname === '/specifications/my-files') {
            dispatch(setBreadcrumbs([
                { to: '/specifications', name: specificationsLabel },
                { to: '/specifications/my-files', name: t('my_files') },
            ]));
        }
        else if (location.pathname.includes('/specifications/folders/')) {
            dispatch(setBreadcrumbs([
                { to: '/specifications', name: specificationsLabel },
                ...folderBreadcrumbs,
            ]));
        }
        else {
            dispatch(setBreadcrumbs([{ to: '/specifications', name: specificationsLabel }]));
        }
        return () => {
            dispatch(clearBreadcrumbs());
        };
    }, [dispatch, t, location.pathname, folderBreadcrumbs]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Dashboard, { headerAside: React.createElement(Switch, null,
                React.createElement(Route, { exact: true, path: `${match.url}/my-files` },
                    React.createElement(HeaderAside, null)),
                React.createElement(Route, { exact: true, path: `${match.url}/folders/:folderId` },
                    React.createElement(HeaderAside, { folders: true }))), asideMenu: React.createElement(Sticky, null,
                React.createElement(Grid, { vp: 24, hp: 16, gap: 8 },
                    React.createElement(Bar, { height: 24, hp: 8 },
                        React.createElement(Caption, { color: "subtle" }, t('common:QUICK_ACCESS'))),
                    React.createElement(Submenu, { groups: [{ id: 'defaultLinks', items: defaultLinks }] }),
                    React.createElement(FoldersTree, { rootFolderId: rootFolder.rootFolderId, folders: foldersTree }))) },
            React.createElement(Grid, { vp: 24, hp: 24, gap: 24 },
                React.createElement(Bar, { aside: React.createElement(Switch, null,
                        React.createElement(Route, { exact: true, path: `${match.url}/my-files` },
                            React.createElement(Filters, null)),
                        React.createElement(Route, { exact: true, path: `${match.url}/folders/:folderId` },
                            React.createElement(Filters, { folders: true }))) },
                    React.createElement(Switch, null,
                        React.createElement(Route, { exact: true, path: `${match.url}/my-files` },
                            React.createElement(Subtitle, { size: "s" }, t('my_files'))),
                        React.createElement(Route, { exact: true, path: `${match.url}/folders/:folderId` },
                            React.createElement(Subtitle, { size: "s" }, folderId === rootFolder.rootFolderId ? t('all_folders') : folderName)))),
                React.createElement(Switch, null,
                    React.createElement(Route, { exact: true, path: `${match.url}/my-files` },
                        React.createElement(AllSpecifications, null)),
                    React.createElement(Route, { exact: true, path: `${match.url}/folders/:folderId` },
                        React.createElement(FolderView, null)),
                    React.createElement(Route, { exact: true, path: match.url }, (rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId) && (React.createElement(Redirect, { to: `${url}/folders/${rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId}` })))))),
        ((_a = layout.showModals) === null || _a === void 0 ? void 0 : _a.some((modal) => modal.name === 'folderCreator')) && (React.createElement(FolderCreator, { parentFolder: folderId }))));
}
export default memo(withRouter(SpecificationsDashboard));
